import React, { useEffect, useState, useCallback } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import { useResponsive } from '../../hooks/useResponsive';

interface AddableMatch {
  id: string;
  location: string;
  date: string;
  time: string;
  team1Name?: string;
  team2Name?: string;
  team1Goals?: number | null;
  team2Goals?: number | null;
  playersCount: number;
}

interface Props {
  isOpen: boolean;
  groupId: string;
  onClose: () => void;
  onAdded: (count: number) => void;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json',
});

export const AddMatchesFromHistoryModal: React.FC<Props> = ({ isOpen, groupId, onClose, onAdded }) => {
  const { isMobile } = useResponsive();
  const [matches, setMatches] = useState<AddableMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const fetchAddable = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/addable-matches`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({ matches: [] }));
      setMatches(Array.isArray(data.matches) ? data.matches : []);
    } catch {
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      fetchAddable();
    }
  }, [isOpen, fetchAddable]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/add-matches`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ matchIds: Array.from(selected) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error');
      onAdded(data.added ?? selected.size);
      onClose();
    } catch {
      // el padre muestra el error via onAdded(-1)? mantenemos simple: no cerramos
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: isMobile ? '10px' : '20px',
      }}
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        style={{
          background: theme.colors.surface, borderRadius: 16,
          padding: isMobile ? 16 : 24, maxWidth: 520, width: '100%',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          border: `1px solid ${theme.colors.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h2 style={{ margin: '0 0 6px 0', color: theme.colors.text, fontSize: 19, fontWeight: 800 }}>
          Agregar partidos al grupo
        </h2>
        <p style={{ margin: '0 0 14px 0', color: theme.colors.textSecondary, fontSize: 13 }}>
          Sumá partidos jugados que organizaste y todavía no están en el grupo. Cuentan para la temporada y las stats.
        </p>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 14 }}>
          {loading ? (
            <p style={{ color: theme.colors.textSecondary }}>Cargando…</p>
          ) : matches.length === 0 ? (
            <p style={{ color: theme.colors.textSecondary }}>No hay partidos para agregar.</p>
          ) : (
            matches.map((m) => {
              const sel = selected.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                    padding: '10px 12px', marginBottom: 8, borderRadius: 10,
                    border: `1px solid ${sel ? theme.colors.primary : theme.colors.border}`,
                    background: sel ? `${theme.colors.primary}14` : theme.colors.background,
                    cursor: 'pointer',
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                    border: `2px solid ${sel ? theme.colors.primary : theme.colors.border}`,
                    background: sel ? theme.colors.primary : 'transparent',
                    color: theme.colors.background, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 13, fontWeight: 900,
                  }}>
                    {sel ? '✓' : ''}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                      {m.location}
                    </span>
                    <span style={{ display: 'block', color: theme.colors.textSecondary, fontSize: 12 }}>
                      {m.date} · {m.time} · {m.playersCount} jugadores
                      {m.team1Goals != null && m.team2Goals != null ? ` · ${m.team1Goals}-${m.team2Goals}` : ''}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1, padding: 12, borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: 'transparent', color: theme.colors.text, fontWeight: 600, fontSize: 15,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={selected.size === 0 || submitting}
            style={{
              flex: 1, padding: 12, borderRadius: 8, border: 'none',
              background: theme.colors.primary, color: theme.colors.background, fontWeight: 700, fontSize: 15,
              cursor: selected.size === 0 || submitting ? 'not-allowed' : 'pointer',
              opacity: selected.size === 0 || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Agregando…' : `Agregar${selected.size > 0 ? ` (${selected.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
